import { Link } from "@tanstack/react-router";
import { ReactComponent as AvatarIcon } from "../../assets/img/avatar.svg";
import { ReactComponent as DateRangeIcon } from "../../assets/img/events/date.svg";
import { ReactComponent as LocationIcon } from "../../assets/img/events/location.svg";
import { ReactComponent as PriceIcon } from "../../assets/img/events/price.svg";
import { Button } from "components/elements";
import { useUserTimezone } from "hooks/useUserTimezone";
import { getMapEmbedUrl, getMapLinkUrl } from "lib/mapLinks";
import { CalendarFriendlyEvent, dateRangeToDisplay } from "./types";

interface EventDetailsBodyProps {
    event: CalendarFriendlyEvent;
    /** Show a Join Meeting button linking to /events/$id/join. Defaults to
     *  true for COMPASS-typed events (the type currently used to back Zoom
     *  meetings). */
    showJoinMeeting?: boolean;
}

/**
 * Body markup for an event's detail card. Used by EventDetailsModal (wrapped
 * in a Radix Dialog) and by the routed /events/$id page (inline).
 */
export function EventDetailsBody({
    event,
    showJoinMeeting = event.type === "COMPASS",
}: EventDetailsBodyProps) {
    const timeZone = useUserTimezone();
    return (
        <>
            <h3 className="mb-3 font-sans text-[22px] font-semibold leading-normal tracking-normal text-[#424242]">
                {event.title}
            </h3>
            {event?.host && (
                <p className="mb-4 flex items-center text-sm">
                    <AvatarIcon className="mr-2 h-8 w-8 text-gray-500" />
                    <span className="mr-1 font-medium">{event.host}</span> is hosting this event
                </p>
            )}
            {event?.url && (
                <p className="mb-4 flex items-center text-sm">
                    <a
                        target="_blank"
                        href={event.url}
                        rel="noreferrer"
                        className="mr-1 font-medium text-blue-500 underline"
                    >
                        Event Website
                    </a>
                </p>
            )}

            <div className="mb-4 flex flex-wrap gap-2">
                <span className="flex items-center gap-2 rounded bg-[#dff1fa] px-3 py-2 font-sans text-[14px] font-normal text-[#424242]">
                    <DateRangeIcon className="h-4 w-4 text-gray-600" />
                    {dateRangeToDisplay(event.start, event?.end, timeZone)}
                </span>
            </div>

            {(() => {
                const mapTarget = {
                    latitude: event.latitude,
                    longitude: event.longitude,
                    location: event.location,
                };
                const mapLink = getMapLinkUrl(mapTarget);
                const mapEmbed = getMapEmbedUrl(mapTarget);
                const showLocationRow = event.location || event.priceRange;
                if (!showLocationRow && !mapEmbed) return null;
                return (
                    <>
                        {showLocationRow && (
                            <div className="mb-4 flex flex-wrap gap-2">
                                {event.location && mapLink && (
                                    <a
                                        href={mapLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 rounded bg-[#dff1fa] px-3 py-2 font-sans text-[14px] font-normal text-[#424242] transition-colors hover:bg-[#c8e4f5]"
                                    >
                                        <LocationIcon className="h-4 w-4 text-gray-600" />
                                        {event.location}
                                    </a>
                                )}
                                {event.priceRange && (
                                    <span className="flex items-center gap-2 rounded bg-[#dff1fa] px-3 py-2 font-sans text-[14px] font-normal text-[#424242]">
                                        <PriceIcon className="h-4 w-4 text-gray-600" />
                                        {event.priceRange}
                                    </span>
                                )}
                            </div>
                        )}
                        {mapEmbed && (
                            <div className="relative mb-4 h-[200px] overflow-hidden rounded-lg border border-[#e6e8ec]">
                                <iframe
                                    title="Event location map"
                                    src={mapEmbed}
                                    className="h-[200px] w-full border-0"
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                                {mapLink && (
                                    <a
                                        href={mapLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Open event location in maps"
                                        className="absolute inset-0 z-10"
                                    />
                                )}
                            </div>
                        )}
                    </>
                );
            })()}

            {event.description && (
                <div className="mb-4">
                    <h4 className="mb-1 font-sans text-[18px] font-semibold leading-normal tracking-normal text-[#424242]">
                        About the event
                    </h4>
                    <div className="mt-3 rounded-lg bg-[#eeeeee] p-4 font-sans text-[14px] font-normal leading-[127%] text-[#424242]/90">
                        {event.description}
                    </div>
                </div>
            )}

            {showJoinMeeting && (
                <div className="mt-4">
                    <Link to="/events/$id/join" params={{ id: String(event.id) }}>
                        <Button variant="primary">Join Meeting</Button>
                    </Link>
                </div>
            )}
        </>
    );
}
